import { type RefObject, useLayoutEffect } from "react";

/**
 * Grow a textarea to fit its content as the user types. Its composer box is
 * what caps the growth (`max-height` + `overflow-y: auto` there), so the
 * scrollbar runs the box's full edge rather than the textarea's.
 */
const fit = (el: HTMLTextAreaElement): void => {
  // Empty drafts get no inline height at all — the stylesheet's min-height
  // rules. Measuring here (mount, HMR) has seen pre-layout scrollHeights.
  if (el.value === "") {
    el.style.height = "";
    return;
  }
  // Collapse first so deleting lines shrinks it back down.
  el.style.height = "auto";
  el.style.height = `${String(el.scrollHeight)}px`;
  // Keep the caret's line in view when the caret is at the end (which typing
  // keeps it): the scroll may live on the textarea or on the composer box
  // wrapping it — bump whichever one overflows.
  if (el.selectionStart === el.value.length) {
    el.scrollTop = el.scrollHeight;
    const box = el.parentElement;
    if (box && box.scrollHeight > box.clientHeight) {
      box.scrollTop = box.scrollHeight;
    }
  }
};

export const useAutosizeTextarea = (
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
): void => {
  useLayoutEffect(() => {
    if (ref.current) {
      fit(ref.current);
    }
  }, [ref, value]);

  // A height computed against one width is wrong at another — a resized
  // drawer, a panel opening, layout settling after mount. Re-fit on any size
  // change; re-fitting to the same height doesn't re-fire the observer.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver(() => {
      fit(el);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [ref]);
};
